// Audit › Logins: every sign-in attempt, to the portal and to tenant admins.
//
// Filters (all in the URL): league_id, email (debounced), ip (debounced),
// result (ok | failed), from / to (inclusive, `tz`). The tiles count the
// whole filtered range when the backend sends `summary`; on a legacy backend
// they only count the loaded page and say so.

import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import type { LoginEvent, LoginListParams, LoginListResponse } from '@/api/types'
import { DateRangeFields, ExportCsvButton, TenantSelect } from '@/components/audit/FilterControls'
import { deviceText, failureReasonText } from '@/components/audit/loginText'
import { dateParam, idParam, useUrlFilters } from '@/components/audit/urlFilters'
import { useCursorPages } from '@/components/audit/useCursorPages'
import { Badge, Button, Card, DataTable, EmptyState, ErrorState, FilterChips, FormField, RelativeTime, SearchInput, SkeletonRows, StatTile, type Column } from '@/components/ui'
import { formatCount, formatDateTime, viewerTimeZone } from '@/lib/format'

const KEYS = ['league_id', 'email', 'ip', 'result', 'from', 'to'] as const
const PAGE = 50

const RESULTS = [
  { value: 'all', label: 'All' },
  { value: 'ok', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
] as const

const COLUMNS: Column<LoginEvent>[] = [
  {
    id: 'when',
    header: 'When',
    cell: (l) => (
      <span className="grid gap-0.5">
        <RelativeTime value={l.created_at} />
        <span className="text-2xs text-subtle tabular">{formatDateTime(l.created_at)}</span>
      </span>
    ),
  },
  {
    id: 'who',
    header: 'Person',
    primary: true,
    cell: (l) => (
      <span className="grid min-w-0 gap-0.5">
        {l.user ? (
          <Link to={`/users/${l.user.id}`} className="break-all font-semibold text-ink hover:text-accent">
            {l.user.name || l.email}
          </Link>
        ) : (
          <span className="break-all font-semibold text-ink">{l.email}</span>
        )}
        {l.user?.name ? <span className="break-all font-mono text-xs text-muted">{l.email}</span> : null}
      </span>
    ),
  },
  { id: 'result', header: 'Result', cell: (l) => (l.success ? <Badge tone="good">Signed in</Badge> : <Badge tone="bad">{failureReasonText(l.failure_reason)}</Badge>) },
  {
    id: 'where',
    header: 'Tenant / host',
    mobileLabel: 'Where',
    cell: (l) =>
      l.league ? (
        <Link to={`/tenants/${l.league.id}/activity`} className="break-all hover:text-accent">
          {l.league.name}
        </Link>
      ) : l.host ? (
        <span className="break-all font-mono text-xs">{l.host}</span>
      ) : (
        <span className="text-subtle">—</span>
      ),
  },
  { id: 'ip', header: 'IP', cell: (l) => <span className="break-all font-mono text-xs text-muted">{l.ip_address ?? '—'}</span> },
  { id: 'device', header: 'Device', cell: (l) => <span className="text-muted" title={l.user_agent ?? undefined}>{deviceText(l.user_agent)}</span> },
]

export default function LoginsTab() {
  const { values: f, set, reset, active } = useUrlFilters(KEYS)
  const rangeError = f.from && f.to && f.from > f.to ? 'The end date is before the start date.' : null
  const result = (RESULTS.find((r) => r.value === f.result)?.value ?? 'all') as (typeof RESULTS)[number]['value']

  const params = useMemo<LoginListParams>(() => {
    const from = dateParam(f.from)
    const to = dateParam(f.to)
    return {
      league_id: idParam(f.league_id),
      email: f.email.trim() || undefined,
      ip: f.ip.trim() || undefined,
      success: f.result === 'ok' ? true : f.result === 'failed' ? false : undefined,
      from,
      to,
      tz: from || to ? viewerTimeZone() : undefined,
    }
  }, [f.league_id, f.email, f.ip, f.result, f.from, f.to])

  const { first, entries, nextCursor, loadMore, loadingMore, moreError } = useCursorPages<LoginEvent, LoginListResponse>(
    rangeError ? null : ['audit', 'logins', params],
    (cursor, signal) => api.audit.logins({ ...params, limit: PAGE, cursor }, signal),
  )

  const summary = first.data?.summary
  const badFilter = first.status === 'error' && isApiError(first.error) && first.error.status === 400

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="grid gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <TenantSelect value={f.league_id} onChange={(v) => set({ league_id: v })} />
          <FormField label="Email">
            {({ id }) => <SearchInput id={id} label="Filter by email" placeholder="Email contains…" value={f.email} onChange={(v) => set({ email: v })} />}
          </FormField>
          <FormField label="IP address">
            {({ id }) => <SearchInput id={id} label="Filter by IP address" placeholder="e.g. 203.0.113" value={f.ip} onChange={(v) => set({ ip: v })} />}
          </FormField>
          <DateRangeFields from={f.from} to={f.to} error={rangeError} onChange={(p) => set(p)} />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <FilterChips label="Filter by result" options={RESULTS.map((r) => ({ value: r.value, label: r.label }))} value={result} onChange={(v) => set({ result: v === 'all' ? '' : v })} />
          <div className="flex flex-wrap items-start gap-2">
            <Button onClick={reset} disabled={!active}>
              Reset
            </Button>
            <ExportCsvButton run={() => api.audit.loginsExport(params)} />
          </div>
        </div>
      </div>

      {summary && !rangeError ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label="Attempts" value={formatCount(summary.total)} />
          <StatTile label="Succeeded" value={formatCount(summary.succeeded)} />
          <StatTile label="Failed" value={formatCount(summary.failed)} tone={summary.failed ? 'bad' : 'default'} />
          <StatTile label="People" value={formatCount(summary.unique_users)} />
        </div>
      ) : null}

      <Card flush className="overflow-hidden">
        {rangeError ? (
          <EmptyState title="Check the dates" description={rangeError} action={<Button size="sm" onClick={() => set({ from: '', to: '' })}>Clear dates</Button>} />
        ) : first.status === 'loading' && !first.data ? (
          <SkeletonRows rows={6} label="Loading sign-ins" />
        ) : first.status === 'error' && !first.data ? (
          <div>
            <ErrorState error={first.error} title={badFilter ? "Those filters aren't valid" : "Couldn't load sign-ins"} onRetry={badFilter ? undefined : first.refetch} />
            {badFilter ? (
              <div className="-mt-8 flex justify-center pb-10">
                <Button size="sm" onClick={reset}>
                  Reset filters
                </Button>
              </div>
            ) : null}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            title={active ? 'No sign-ins match' : 'No sign-ins yet'}
            description={active ? 'Nothing matches these filters.' : 'Sign-in attempts appear here.'}
            action={active ? <Button size="sm" onClick={reset}>Reset filters</Button> : undefined}
          />
        ) : (
          <>
            <DataTable caption="Sign-ins" rows={entries} columns={COLUMNS} rowKey={(l) => l.id} busy={first.isFetching} />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 sm:px-5">
              <p className="text-xs text-muted tabular">
                {entries.length} shown{nextCursor == null ? ', that’s everything that matches' : ''}
              </p>
              {moreError ? (
                <p role="alert" className="text-xs text-bad">
                  Couldn&apos;t load more: {moreError}
                </p>
              ) : null}
              {nextCursor != null ? (
                <Button size="sm" onClick={loadMore} loading={loadingMore}>
                  Load more
                </Button>
              ) : null}
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
